<template>
  <div
    class="search-bar"
    :class="{'search-bar__active': search !== ''}"
  >
    <div class="search-bar__input-wrapper">
      <input
        v-model="search"
        class="site-header__search-input"
        type="search"
        :placeholder="$string('general.site_header.search_placeholder')"
        @keyup="handleSearchInput($event)"
        @click="openSearchBlock"
      >

      <button
        v-if="search !== ''"
        class="search-bar__clear icon-button"
        type="button"
        @click="search = ''"
      >
        <span
          class="visually-hidden"
          v-text="$string('general.site_header.clear')"
        />

        <icon-close />
      </button>
    </div>
    <div
      v-if="searchResults || trendingProducts"
      class="search-bar__quicksearch"
    >
      <overlay
        key="quicksearch"
        block-class="quicksearch"
        direction="up"
        :namespace="namespace"
        type="modal"
      >
        <template #body>
          <div class="search-bar__suggestions">
            <h2
              v-if="searchResults"
              class="text-heading-3xs mb-xl-desktop mb-l"
              v-text="$string('search.suggestions.label')"
            />
            <h2
              v-else
              class="text-heading-3xs mb-xl-desktop mb-l"
              v-text="$string('search.popular.label')"
            />
            <div v-if="search !== ''">
              <template v-if="searchSuggestions.length > 0">
                <div
                  v-for="(suggestion, index) of searchSuggestions"
                  :key="`search-suggestion-${index}`"
                  class="quicksearch__suggestions"
                >
                  <a
                    class="text-body-l-regular-desktop text-body-xl-regular text-link-hover"
                    :href="searchPathname(suggestion.suggest)"
                    v-html="suggestion.suggest"
                  />
                </div>
              </template>
              <p
                v-else
                class="text-heading-3xs pt-s"
                v-text="$string('search.suggestions.no_suggestions')"
              />
            </div>
            <div v-else>
              <template v-if="trendingProducts.length > 0">
                <div
                  v-for="(popular, index) of trendingProducts"
                  :key="`search-suggestion-${index}`"
                  class="quicksearch__suggestions"
                >
                  <a
                    class="text-body-l-regular-desktop text-body-xl-regular text-link-hover"
                    :href="formatUrl(popular.url)"
                    v-text="popular.title"
                  />
                </div>
              </template>
              <p
                v-else
                class="text-heading-3xs pt-s"
                v-text="$string('search.suggestions.no_suggestions')"
              />
            </div>
          </div>
          <div
            v-if="searchResults"
            class="search-bar__results"
          >
            <h2
              class="text-heading-3xs"
              v-text="$string('search.results.label')"
            />
            <template v-if="searchResults.length > 0">
              <a
                v-for="(product, index) of searchResults"
                :key="`search-${index}`"
                class="search-bar__result-card"
                :href="formatUrl(product.url)"
              >
                <div class="quicksearch__image">
                  <responsive-image
                    :image="product.featuredImage"
                    min-max="200-800"
                    ratio="4:3"
                  />
                </div>
                <div class="quicksearch__details">
                  <h3
                    class="text-heading-s"
                    v-text="product.title"
                  />
                  <product-prices
                    class="quicksearch__price"
                    class-price="text-other-price-small"
                    class-compare-at-price="text-other-sale-s"
                    :available="product?.available"
                    :compare-at-price="product?.compareAtPrice?.amount"
                    :price="product?.price?.amount"
                  />
                </div>
              </a>
            </template>
            <p
              v-else
              class="text-heading-3xs pt-s"
              v-text="$string('search.results.no_results')"
            />
          </div>
          <div
            v-else
            class="search-bar__results"
          >
            <div class="search-bar__trending-toggle">
              <button
                class="text-heading-3xs icon-button"
                type="button"
                :class="{'active': viewTrending}"
                @click="viewTrending = true"
                v-text="$string('search.trending.label')"
              />
              <button
                class="text-heading-3xs icon-button"
                type="button"
                :class="{'active': !viewTrending}"
                @click="viewTrending = false"
                v-text="recentlyViewedLabel"
              />
            </div>

            <div
              v-if="viewTrending"
              class="search-bar__additional-results"
            >
              <template v-if="trendingProducts.length > 0">
                <a
                  v-for="(product, index) of trendingProducts"
                  :key="`search-${index}`"
                  class="search-bar__result-card"
                  :href="formatUrl(product.url)"
                >
                  <div class="quicksearch__image">
                    <responsive-image
                      :image="product.featuredImage"
                      min-max="200-800"
                      ratio="4:3"
                    />
                  </div>
                  <div class="quicksearch__details">
                    <h3
                      class="text-heading-s"
                      v-text="product.title"
                    />
                    <product-prices
                      class="quicksearch__price"
                      class-price="text-other-price-small"
                      :available="product?.available"
                      :compare-at-price="product?.compareAtPrice?.amount"
                      :price="product?.price?.amount"
                    />
                  </div>
                </a>
              </template>
              <p
                v-else
                class="text-heading-3xs pt-s"
                v-text="$string('search.trending.no_results')"
              />
            </div>
            <div
              v-if="!viewTrending"
              class="search-bar__additional-results"
            >
              <template v-if="recentlyViewed.length > 0">
                <a
                  v-for="(product, index) of recentlyViewed"
                  :key="`search-${index}`"
                  class="search-bar__result-card"
                  :href="formatUrl(product.url)"
                >
                  <div class="quicksearch__image">
                    <responsive-image
                      :image="product.featuredImage"
                      min-max="200-800"
                      ratio="4:3"
                    />
                  </div>
                  <div class="quicksearch__details">
                    <h3
                      class="text-heading-s"
                      v-text="product.title"
                    />
                    <product-prices
                      class="quicksearch__price"
                      class-price="text-other-price-small"
                      :available="product?.available"
                      :compare-at-price="product?.compareAtPrice?.amount"
                      :price="product?.price?.amount"
                    />
                  </div>
                </a>
              </template>
              <p
                v-else
                class="text-heading-3xs pt-s"
                v-text="$string('search.recent.no_results')"
              />
            </div>
          </div>
        </template>
      </overlay>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Search bar (search-bar)
 * -----------------------------------------------------------------------------
 * Global search bar on navigation.
 *
 */
import ProductPrices from '~global/product-prices/product-prices'
import ResponsiveImage from '~global/images/responsive-image'
import Overlay from '~global/overlay/overlay'
import IconClose from '~icons/directional-navigation/close.svg'

import { fetchRecentlyViewed, fetchSearchQuery, fetchTrendingProducts, initKlevu, sanitizedCollectionObject } from '~/apis/klevu'
import {
  applyFilterWithManager,
  FilterManager,
// eslint-disable-next-line import/no-unresolved
} from '@klevu/core'

import { debounce } from '~/helpers/general'
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'SearchBar',

  components: {
    IconClose,
    ProductPrices,
    ResponsiveImage,
    Overlay,
  },

  props: {
    klevu: {
      type: Object,
      default: () => ({}),
    },
    namespace: {
      type: String,
      default: 'quicksearch',
    },
  },

  data() {
    return {
      cnvsGlobal: true,
      search: '',
      searchResults: false,
      trendingProducts: null,
      filterManager: new FilterManager(),
      searchSuggestions: [],
      recentlyViewed: null,
      viewTrending: true,
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
    }),

    recentlyViewedLabel() {
      const label = this.$string('search.recent.label')

      if (!this.recentlyViewed) {
        return label
      }

      return `${label} (${this.recentlyViewed.length})`
    },
  },

  watch: {
    search(value) {
      if (value === '') {
        this.searchResults = false
        return
      }

      this.updateSearchInput(value)
    },
  },

  created() {
    this.updateSearchInput = debounce((value) => {
      this.search = value
      this.fetchSearchResults()
    }, 300)
  },

  async mounted() {
    await initKlevu(this.klevu.url, this.klevu.key)
    this.fetchTrending()
    this.fetchRecentlyViewed()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      closeOverlay: 'overlays/close',
      openOverlay: 'overlays/open',
    }),

    /**
     * Format url to include market handle url.
     * @param {String} url - Url string.
     * @returns {String}
     */
    formatUrl(url) {
      const pathname = new URL(url).pathname
      const primary = cnvs.strings.defaultLocalization.primaryMarket
      const country = cnvs.store.country.toLowerCase()
      const language = cnvs.store.language.toLowerCase()
      const isPrimaryDomain = primary.domain.toLowerCase() === country
      const isPrimaryLocale = primary.locale.toLowerCase() === language
      let updatedPathname = `/${language}-${country}${pathname}`

      if (isPrimaryDomain) {
        updatedPathname = updatedPathname.replace(`-${country}`, '')

        if (isPrimaryLocale) {
          updatedPathname = updatedPathname.replace(`/${language}`, '')
        }
      }

      return updatedPathname
    },

    async fetchSearchResults() {
      const searchResults = await fetchSearchQuery(
        this.search,
        [
          {
            id: 'search',
            limit: 2,
          },
        ],
      )

      if (!searchResults) {
        return
      }

      const { result, suggestion } = searchResults
      const collection = result.records

      this.searchResults = sanitizedCollectionObject(collection)
      this.searchSuggestions = suggestion
    },

    async fetchTrending() {
      if (this.trendingProducts) {
        return
      }

      const trendingResults = await fetchTrendingProducts(
        {
          id: 'trending',
          limit: 3,
        },
        applyFilterWithManager(this.filterManager),
      )

      if (!trendingResults) {
        return
      }

      this.trendingProducts = sanitizedCollectionObject(trendingResults.records)
    },

    async fetchRecentlyViewed() {
      if (this.recentlyViewed) {
        return
      }

      const viewedResults = await fetchRecentlyViewed(
        {
          id: 'recent',
          limit: 3,
        },
      )

      if (!viewedResults) {
        return
      }

      const selectedRecentProducts = viewedResults.records.slice(0, 3)
      this.recentlyViewed = sanitizedCollectionObject(selectedRecentProducts)
    },

    searchPathname(search) {
      const finalSuggestions = search.replace('<b>', '').replace('</b>', '')
      const param = encodeURIComponent(finalSuggestions)

      const primary = cnvs.strings.defaultLocalization.primaryMarket
      const country = cnvs.store.country.toLowerCase()
      const language = cnvs.store.language.toLowerCase()
      const isPrimaryDomain = primary.domain.toLowerCase() === country
      const isPrimaryLocale = primary.locale.toLowerCase() === language
      let updatedPathname = `/${language}-${country}`

      if (isPrimaryDomain) {
        updatedPathname = updatedPathname.replace(`-${country}`, '')

        if (isPrimaryLocale) {
          updatedPathname = updatedPathname.replace(`/${language}`, '')
        }
      }
      return `${updatedPathname}/search?q=${param}`
    },

    handleSearchInput(event) {
      if (event.code === 'Tab' || event.code === 'ShiftLeft') {
        return
      }

      if (this.getActiveOverlay !== this.namespace) {
        this.openSearchBlock()
      }

      if (event.code !== 'Enter') {
        return
      }

      const pathname = this.searchPathname(event.target.value)
      const { origin } = window.location

      window.location = `${origin}${pathname}`
    },

    openSearchBlock() {
      this.openOverlay({
        ignoreDismissed: true,
        namespace: this.namespace,
      })
    },
  },
}
</script>

<style lang="scss">
  @import './search-bar';
</style>
